<template>
  <v-dialog v-model="isVisible" max-width="600px" persistent>
    <v-card>
      <v-card-title class="popup-header">
        <span class="headline white-text">Editar Motivos de Ausencias</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closePopup" :disabled="loading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="popup-content">
        <p class="subtitle-text">
          Gestiona la lista de motivos de ausencias disponibles para los empleados.
        </p>

        <div class="reasons-list">
          <div
            v-for="(reason, index) in reasons"
            :key="index"
            class="reason-item"
          >
            <v-text-field
              v-model="reasons[index]"
              outlined
              dense
              label="Motivo de ausencia"
              :disabled="loading"
            >
              <template v-slot:append>
                <v-btn
                  icon
                  small
                  color="error"
                  @click="removeReason(index)"
                  :disabled="loading"
                  title="Eliminar motivo"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </div>

          <v-btn
            color="primary"
            outlined
            block
            @click="addReason"
            :disabled="loading"
            class="add-reason-btn"
          >
            <v-icon left>mdi-plus</v-icon>
            Agregar Motivo
          </v-btn>
        </div>
      </v-card-text>

      <v-card-actions class="popup-actions">
        <v-btn color="grey" text @click="closePopup" :disabled="loading">
          Cancelar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="saveReasons"
          :loading="loading"
          :disabled="!hasChanges"
        >
          Guardar Cambios
        </v-btn>
      </v-card-actions>

      <!-- Snackbar -->
      <v-snackbar
        v-model="snackbar.visible"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
      >
        <b>{{ snackbar.text }}</b>
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import { client } from '@/api/client';

export default {
  name: 'AbsenceReasonsPopup',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    companydbId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isVisible: false,
      reasons: [],
      originalReasons: [],
      loading: false,
      snackbar: {
        visible: false,
        text: '',
        color: 'error',
        timeout: 3000
      }
    };
  },
  computed: {
    hasChanges() {
      if (this.reasons.length !== this.originalReasons.length) {
        return true;
      }
      return !this.reasons.every((reason, index) => reason === this.originalReasons[index]);
    }
  },
  watch: {
    visible(newValue) {
      this.isVisible = newValue;
      if (newValue) {
        this.loadReasons();
      }
    },
    isVisible(newValue) {
      if (!newValue) {
        this.$emit('close');
      }
    }
  },
  methods: {
    showSnackbar(color, text) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.visible = true;
    },

    async loadReasons() {
      this.loading = true;
      try {
        const response = await client.get(`/companydb/${this.companydbId}/absence-reasons/`);

        // Si no hay motivos, cargar los motivos por defecto
        const loadedReasons = response.data.absence_reasons || [];
        if (loadedReasons.length === 0) {
          this.reasons = ['Médico', 'Personal', 'Familiar', 'Trámite', 'Otros'];
        } else {
          this.reasons = loadedReasons;
        }

        this.originalReasons = [...this.reasons];
      } catch (error) {
        console.error('Error al cargar motivos:', error);
        // Si hay error, cargar motivos por defecto
        this.reasons = ['Médico', 'Personal', 'Familiar', 'Trámite', 'Otros'];
        this.originalReasons = [];
        this.showSnackbar('warning', 'Cargando motivos por defecto');
      } finally {
        this.loading = false;
      }
    },

    addReason() {
      this.reasons.push('');
    },

    removeReason(index) {
      this.reasons.splice(index, 1);
    },

    async saveReasons() {
      // Filtrar motivos vacíos
      const filteredReasons = this.reasons.filter(reason => reason && reason.trim() !== '');

      if (filteredReasons.length === 0) {
        this.showSnackbar('warning', 'Debes agregar al menos un motivo de ausencia');
        return;
      }

      this.loading = true;
      try {
        await client.put(`/companydb/${this.companydbId}/absence-reasons/update/`, {
          absence_reasons: filteredReasons
        });

        this.showSnackbar('success', 'Motivos de ausencia actualizados correctamente');
        this.originalReasons = [...filteredReasons];
        this.reasons = [...filteredReasons];

        // Emitir evento para que el componente padre sepa que se guardaron los cambios
        this.$emit('saved', filteredReasons);

        // Cerrar el popup después de un breve delay
        setTimeout(() => {
          this.closePopup();
        }, 1000);
      } catch (error) {
        console.error('Error al guardar motivos:', error);
        console.error('CompanyDB ID:', this.companydbId);
        console.error('Error response:', error.response);

        // Mostrar mensaje de error más detallado
        let errorMsg = 'Error al guardar los motivos de ausencias';
        if (error.response && error.response.data) {
          if (error.response.data.error) {
            errorMsg = error.response.data.error;
          } else if (error.response.data.detail) {
            errorMsg = error.response.data.detail;
          }
        }
        this.showSnackbar('error', errorMsg);
      } finally {
        this.loading = false;
      }
    },

    closePopup() {
      if (!this.loading) {
        this.isVisible = false;
        this.reasons = [];
        this.originalReasons = [];
      }
    }
  }
};
</script>

<style scoped>
.popup-header {
  background-color: var(--Primary-700, #7339B7);
  color: white;
  padding: 16px 24px;
}

.popup-header .headline {
  color: white !important;
}

.popup-header .v-icon {
  color: white !important;
}

.popup-content {
  padding: 32px 24px;
  min-height: 300px;
  max-height: 60vh;
  overflow-y: auto;
}

.subtitle-text {
  color: #666;
  margin-bottom: 24px;
  font-size: 14px;
}

.reasons-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.reason-item {
  width: 100%;
}

.add-reason-btn {
  margin-top: 8px;
  border-style: dashed;
}

.popup-actions {
  padding: 16px 24px;
  background-color: #f5f5f5;
}

/* Responsive */
@media (max-width: 600px) {
  .popup-content {
    padding: 16px;
  }
}
</style>
